import React, { Fragment, useCallback, useEffect, useState } from "react";
import UserInfoSummary from "./UserInfoSummary";
import UserEmailsTable from "./UserEmailsTable";
import UserAliasesTable from "./UserAliasesTable";
import ViewSection from "./ViewSection";
import {
    faClock,
    faEnvelope,
    faIdCardAlt,
    faInfoCircle,
    faKey,
    faSignIn,
    faUser,
} from "@fortawesome/free-solid-svg-icons";
import UserRoleSummary from "./UserRoleSummary";
import { useLoaderData, useRevalidator } from "react-router-dom";
import { defineMessage, FormattedMessage } from "react-intl";
import { useFetch } from "./FetchProvider";
import UserSessionTable from "./user_session/UserSessionTable";
import user, { userGenderTranslations } from "../descriptors/user";
import { TitledContent } from "./TitledContent";
import { Button, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import ResourceFormButton from "./ResourceFormButton";
import AddRoleButton from "./AddRoleButton";
import NotSetValue from "./NotSetValue";
import { useUserData } from "../context/UserDataProvider";

export const userTranslations = {
    user: {
        no_name: defineMessage({
            id: "user.no_name",
            defaultMessage: "Name not set",
        }),
        no_birth_date: defineMessage({
            id: "user.no_birth_date",
            defaultMessage: "No birth date",
        }),
        given_name: defineMessage({
            id: "user.given_name",
            defaultMessage: "Given name",
        }),
        family_name: defineMessage({
            id: "user.family_name",
            defaultMessage: "Family name",
        }),
        gender: defineMessage({
            id: "user.gender",
            defaultMessage: "Gender",
        }),
        birthdate: defineMessage({
            id: "user.birthdate",
            defaultMessage: "Birth date",
        }),
        locale: defineMessage({
            id: "user.locale",
            defaultMessage: "Preferred locale",
        }),
        basic_data: defineMessage({
            id: "user.basic_data",
            defaultMessage: "Basic data",
        }),
        oidc_sub: defineMessage({
            id: "user.oidc_sub",
            defaultMessage: "OpenID Connect identifier",
        }),
        aliases: defineMessage({
            id: "user.aliases",
            defaultMessage: "Login names",
        }),
        user_infos: defineMessage({
            id: "user.user_infos",
            defaultMessage: "$users.user_infos",
        }),
        roles: defineMessage({
            id: "user.roles",
            defaultMessage: "$users.roles",
        }),
        emails: defineMessage({
            id: "user.emails",
            defaultMessage: "Emails",
        }),
        sessions: defineMessage({
            id: "user.sessions",
            defaultMessage: "Sessions",
        }),
    },
} as const;

const baseApiUrl = "/api/user/totp";

const TOtpSettings = ({ id }: { id: number }) => {
    const [enabled, setEnabled] = useState<boolean | null>(null);
    const fetch = useFetch();
    const userData = useUserData();
    const isOperator = userData.hasPermission("system");
    const suffix = isOperator && id !== userData.userId ? `/${id}` : "";

    const clear = async () => {
        const response = await fetch(baseApiUrl + `/disable${suffix}`, { method: "post" });
        if (response.status === 200 && response.status < 500) setEnabled(false);
        else alert("Unexpected error");
    };

    const fetchData = useCallback(async () => {
        const response = await fetch(baseApiUrl + `/info${suffix}`);
        if (response.status !== 200) {
            alert("Unexpected error");
            return;
        }

        const data = await response.json();

        setEnabled(data.enabled);
    }, [fetch, suffix]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (enabled === null) return <div>...</div>;

    const DisableButton = () => (
        <Button onClick={(_) => clear()}>
            <FormattedMessage id="account.totp.disable_button" defaultMessage="Disable" />
        </Button>
    );

    const StateLabel = () => (
        <span style={{ marginRight: 15 }}>
            {enabled ? (
                <FormattedMessage id="account.totp.enabled" defaultMessage="Enabled" />
            ) : (
                <FormattedMessage id="account.totp.disabled" defaultMessage="Disabled" />
            )}
        </span>
    );

    return (
        <div>
            <StateLabel /> {enabled ? <DisableButton /> : null}
        </div>
    );
};

const UserView = () => {
    const userData = useUserData();
    const renderers = user.renderers?.read;
    const revalidator = useRevalidator();
    const data = useLoaderData() as any;

    let name: any = data.name;
    if (!name) {
        if (data.aliases.length) name = data.aliases[0].name;
        else name = <NotSetValue label={defineMessage({ id: "user.no_name", defaultMessage: "Name not set" })} />;
    }

    const basicDataRows = [];

    for (const name of ["given_name", "family_name", "gender", "birthdate", "locale"]) {
        let value = data[name];
        if (name in renderers) value = renderers[name](value, data);
        basicDataRows.push(
            <Fragment key={name}>
                <Grid size={4}>
                    <FormattedMessage {...userTranslations.user[name]} />
                </Grid>
                <Grid size={8}>{value}</Grid>
            </Fragment>,
        );
    }

    basicDataRows.push(
        <Fragment key="totp">
            <Grid size={4}>
                <FormattedMessage id="account.totp.settings_short" defaultMessage="Two factor authentication" />
            </Grid>
            <Grid size={8}>
                {(userData.hasPermission("system") || userData.userId === data.id) && <TOtpSettings id={data.id} />}
            </Grid>
        </Fragment>,
    );

    return (
        <>
            <TitledContent title={name}>
                <Typography variant="subtitle1">
                    <span>
                        <FormattedMessage {...userGenderTranslations[data.gender]} />
                    </span>
                    , {data.birthdate || <FormattedMessage {...userTranslations.user.no_birth_date} />}
                </Typography>
                <Stack spacing={5} mt={5}>
                    <ViewSection
                        data-section="basic-data"
                        icon={faUser}
                        title={userTranslations.user.basic_data}
                        button={
                            <ResourceFormButton
                                resourceName="managed_user"
                                action="update"
                                actionLabel="managed-user-edit"
                                dialogTitle={defineMessage({
                                    id: "managed_user.update",
                                    defaultMessage: "$simplified_user.update",
                                })}
                                id={data.id}
                                buttonType="section-icon"
                            />
                        }
                    >
                        <Grid container spacing={2}>
                            {...basicDataRows}
                        </Grid>
                    </ViewSection>
                    <ViewSection data-section="oidc-sub" icon={faIdCardAlt} title={userTranslations.user.oidc_sub}>
                        <div>{data["user_sub"]["user_sub"]}</div>
                    </ViewSection>
                    <ViewSection
                        data-section="aliases"
                        icon={faSignIn}
                        title={userTranslations.user.aliases}
                        button={
                            <ResourceFormButton
                                resourceName="user_alias"
                                action="create"
                                dialogTitle={defineMessage({
                                    id: "user_alias.create",
                                    defaultMessage: "Create",
                                })}
                                buttonType="section-icon"
                                extraRequestData={{
                                    user: data.id,
                                    owner_id: data.id,
                                    owner_type: "USER",
                                }}
                            />
                        }
                    >
                        <UserAliasesTable rows={data.aliases} showOwnerType={true} />
                    </ViewSection>
                    <ViewSection data-section="user-infos" icon={faInfoCircle} title={userTranslations.user.user_infos}>
                        <UserInfoSummary userInfos={data["user_sub"]["user_infos"]} />
                    </ViewSection>
                    <ViewSection
                        data-section="roles"
                        icon={faKey}
                        title={userTranslations.user.roles}
                        button={<AddRoleButton userSubId={data["user_sub"].id} />}
                    >
                        <UserRoleSummary
                            userGrants={data.user_sub["user_grants"]}
                            userSubId={data.user_sub.id}
                            onUpdateRole={(changed) => {
                                if (changed) revalidator.revalidate();
                            }}
                        />
                    </ViewSection>
                    <ViewSection
                        data-section="emails"
                        icon={faEnvelope}
                        title={userTranslations.user.emails}
                        button={
                            <ResourceFormButton
                                resourceName="user_email"
                                action="create"
                                dialogTitle={defineMessage({
                                    id: "user_email.create",
                                    defaultMessage: "Add email",
                                })}
                                buttonType="section-icon"
                                extraRequestData={{ user: data.id }}
                            />
                        }
                    >
                        <UserEmailsTable rows={data.emails} />
                    </ViewSection>
                    <ViewSection
                        data-section="sessions"
                        icon={faClock}
                        title={userTranslations.user.sessions}
                        button={
                            <ResourceFormButton
                                resourceName="token"
                                action="create"
                                dialogTitle={defineMessage({
                                    id: "token.create",
                                    defaultMessage: "Add session",
                                })}
                                buttonType="section-icon"
                                extraRequestData={{
                                    refresh_token_expires_in: 0,
                                    user_sub: data["user_sub"]["user_sub"],
                                }}
                            />
                        }
                    >
                        <UserSessionTable userSub={data["user_sub"]["user_sub"]} />
                    </ViewSection>
                </Stack>
            </TitledContent>
        </>
    );
};

export default UserView;
